import angular from 'angular';

import template from './list.html';

const errorString = 'akamList: Filtering requires either an onFilter callback or a text property';

const listTemplate = `
  <ul vs-repeat vs-excess="10" class="akam-list-viewport">
    <li ng-repeat="item in akamList.filteredItems track by akamList.idPropertyFn(item)" class="item"
        ng-class="{selected: akamList.selectedItemsMap[akamList.idPropertyFn(item)]}">
      <span class="column-draggable"><i class="aci-grip"></i></span>
      <label class="column-selectable">
        <input type="checkbox"
              ng-model="akamList.selectedItemsMap[akamList.idPropertyFn(item)]"
              ng-disabled="akamList.isDisabled"
              ng-true-value="{{ item }}"
              id="{{ ::akamList.id }}-{{ ::akamList.idPropertyFn(item)}}-item"
              ng-change="akamList.toggleSelected()">
        <span class="label"></span>
      </label>
      <span class="column-expandable"><i class="aci-arrow-right"></i></span>
      <akam-list-template></akam-list-template>
    </li>
  </ul>
`;

const TRISTATE_STATES = {
  checked: 'checked',
  unchecked: 'unchecked',
  indeterminate: 'indeterminate'
};

class List {

  static get $inject() {
    return ['$q', '$parse', '$element', '$scope', 'numberFilter', 'uuid', '$attrs'];
  }

  constructor($q, $parse, $element, $scope, numberFilter, uuid, $attrs) {
    this.$q = $q;
    this.$parse = $parse;
    this.$element = $element;
    this.$scope = $scope;
    this.numberFilter = numberFilter;
    this.uuid = uuid;
    this.$attrs = $attrs;
  }

  $onInit() {
    this.idPropertyFn = this.$parse(this.idProperty || 'id');
    this.textPropertyFn = this.$parse(this.textProperty);
    this.filter = '';
    this.notSelectable = angular.isDefined(this.$attrs.notSelectable);
    this.sortable = angular.isDefined(this.$attrs.sortable);
    this.expandable = angular.isDefined(this.$attrs.expandable);
    this.draggable = angular.isDefined(this.$attrs.draggable);
    this.filterable = !angular.isDefined(this.$attrs.notFilterable);

    if (this.filterable) {
      this.initMatchingFn();
    }

    this.id = this.uuid.uid();

    this.noItemsMessage = this.noItemsMessage || 'components.list.no-data-message';
    this.noFilterResultsMessage = this.noFilterResultsMessage ||
      'components.list.no-filter-results';
    this.filterPlaceholder = this.filterPlaceholder || 'components.list.filter-placeholder';
    this.failureMessage = this.failureMessage || 'components.list.failure-message';

    this.filteredItems = [];
    this.pristineItems = [];
    this.$q.when(this.items).then(items => {
      this.filteredItems = items.hasOwnProperty('data') ? items.data : items;
      this.pristineItems = this.filteredItems;
      this.updateVisibleSelectedItems();
      this.updateCounts();
      this.updateTriStateSelectState();
    });
    this.selectedItemsMap = {};

    this.$scope.$watch('$ctrl.items', angular.bind(this, this.loadingFn));
    this.$scope.$watch('$ctrl.ngModel.$modelValue', angular.bind(this, this.setSelectedItems));
  }

  initMatchingFn() {
    if (angular.isFunction(this.onFilter)) {
      this.match = item => this.onFilter({item, term: this.filter});
    } else if (this.textProperty) {
      this.match = item => this.textPropertyFn(item).toString().toLowerCase().includes(this.filter.toLowerCase());
    } else {
      throw new Error(errorString);
    }
  }

  loadingFn() {

    const doneLoading = items => {
      this.filteredItems = items.hasOwnProperty('data') ? items.data : items;
      this.pristineItems = this.filteredItems;
      this.updateVisibleSelectedItems();
      this.updateCounts();
      this.updateTriStateSelectState();
      this.loading = false;
      return items;
    };

    const failedLoading = (items) => {
      this.failed = true;
      this.indeterminateProgressLabel = this.failureMessage;
      return items;
    };

    this.loading = true;
    this.failed = false;
    if (this.loadingMessage) {
      this.indeterminateProgressLabel = this.loadingMessage;
    }
    this.$q.when(this.items)
      .then(doneLoading)
      .catch(failedLoading);
  }

  // this is called when we update selected items inside this component and need to reconstruct
  // the model
  toggleSelected() {
    const newModel = [];
    angular.forEach(this.selectedItemsMap, value => {
      if (value) {
        newModel.push(value);
      }
    });

    if (newModel.length === 0) {
      this.shouldViewSelected = false;
    }

    this.ngModel.$setViewValue(newModel);
    this.ngModel.$setTouched();
    this.ngModel.$setDirty();
    this.filterList();
  }

  // this is called when the model changes outside of this component
  setSelectedItems() {
    this.selectedItemsMap = {};
    angular.forEach(this.ngModel.$modelValue,
        item => this.selectedItemsMap[this.idPropertyFn(item)] = item);
    this.filterList();
  }

  filterList() {
    this.filteredItems = this.pristineItems;

    if (this.shouldViewSelected) {
      this.filteredItems = this.ngModel.$modelValue || this.ngModel.$viewValue;
    }

    if (this.filterable) {
      this.filteredItems = this.filteredItems.filter(item => this.match(item));
    }

    this.updateVisibleSelectedItems();
    this.updateCounts();
    this.updateTriStateSelectState();
  }

  // this keeps the list of visible selected items intact so that we know what items to add
  // when the user clicks "add all". This can be inlined if we don't have to display the number
  // of visible selected items next to "Clear All" and "View Selected"
  updateVisibleSelectedItems() {
    const isSelected = item => this.selectedItemsMap[this.idPropertyFn(item)];
    this.visibleSelectedItems = this.filteredItems.filter(item => isSelected(item));
  }

  // small optimization to avoid using a pipe filter in the view.
  updateCounts() {
    this.numAvailable = this.numberFilter(this.filteredItems.length);
    this.numAvailablePristine = this.numberFilter(this.pristineItems.length);
    this.numSelected = Object.keys(this.selectedItemsMap).length;
    this.numSelectedVisible = this.numberFilter(this.visibleSelectedItems.length);
    this.numUnselectedVisible = this.numberFilter(this.filteredItems.length -
      this.visibleSelectedItems.length);
  }

  get isViewSelectedOnlyDisabled() {
    const disabled = this.isDisabled || !Array.isArray(this.pristineItems) ||
      this.pristineItems.length === 0 || Object.keys(this.selectedItemsMap).length === 0;
    if (disabled) {
      this.shouldViewSelected = false;
    }
    return disabled;
  }

  viewSelected() {
    if (this.shouldViewSelected) {
      this.updateVisibleSelectedItems();
      this.filteredItems = this.visibleSelectedItems;
      this.updateCounts();
      this.updateTriStateSelectState();
    } else {
      this.filterList();
    }
    this.scrollToTop();
  }

  selectAll() {
    if (!this.shouldViewSelected && !this.isDisabled) {
      this.filteredItems.forEach(item => this.selectedItemsMap[this.idPropertyFn(item)] = item);
      this.triStateSelectState = TRISTATE_STATES.checked;
      this.toggleSelected();
    }
  }

  clearAll() {
    if (!this.isDisabled) {
      this.updateVisibleSelectedItems();
      this.visibleSelectedItems.forEach(item =>
          Reflect.deleteProperty(this.selectedItemsMap, this.idPropertyFn(item))
      );
      this.triStateSelectState = TRISTATE_STATES.unchecked;
      this.toggleSelected();
    }
  }

  updateTriStateSelectState() {
    if (this.numSelectedVisible === '0') {
      this.triStateSelectState = TRISTATE_STATES.unchecked;
      return;
    }
    if (this.numUnselectedVisible === '0') {
      this.triStateSelectState = TRISTATE_STATES.checked;
      return;
    }
    this.triStateSelectState = TRISTATE_STATES.indeterminate;
  }

  toggleSelectAll() {
    if (this.triStateSelectState === TRISTATE_STATES.unchecked) {
      this.selectAll();
    } else {
      this.clearAll();
    }
  }

  sort() {
    this.filteredItems.reverse();
  }

  messages() {
    return {
      noData: this.pristineItems && this.pristineItems.length === 0 && !this.loading,
      noFiltered: this.filteredItems && this.filteredItems.length === 0 && !this.loading
    };
  }

  // HACK! Reset the scroll position when the filter is typed in so items are visible
  // SEE https://github.com/kamilkp/angular-vs-repeat/issues/116
  scrollToTop() {
    this.$element[0].querySelector('.akam-list-viewport').scrollTop = 0;
  }

}

function akamList($compile) {
  return {
    restrict: 'E',
    scope: {},
    require: 'ngModel',
    bindToController: {
      items: '=',
      idProperty: '@',
      notFilterable: '@',
      textProperty: '@',
      filterPlaceholder: '@',
      noFilterResultsMessage: '@',
      loadingMessage: '@',
      failureMessage: '@',
      noItemsMessage: '@',
      striped: '@',
      draggable: '=?',
      notSelectable: '=?',
      expandable: '=?',
      sortable: '=?',
      isDisabled: '=?',
      onFilter: '&?'
    },
    template(element, attributes) {
      const tpl = angular.element(template);
      let itemTemplate = '';

      // custom markup
      if (element.html().trim()) {
        itemTemplate = element.html();
      }
      tpl.find('akam-list-template-placeholder')
        .html(`<akam-list-template>${itemTemplate}</akam-list-template>`);

      // add striped table
      if (angular.isDefined(attributes.striped)) {
        element.addClass('striped');
      }

      // identify as draggable
      if (angular.isDefined(attributes.draggable)) {
        element.addClass('draggable');
      }

      // identify as not selectable
      if (angular.isDefined(attributes.notSelectable)) {
        element.addClass('not-selectable');
      }

      // identify as expandable
      if (angular.isDefined(attributes.expandable)) {
        element.addClass('expandable');
      }

      // identify as sortable
      if (angular.isDefined(attributes.sortable)) {
        element.addClass('sortable');
      }

      // remove filter, if necessary
      if (angular.isDefined(attributes.notFilterable)) {
        angular.element(tpl[0].querySelector('.filter')).remove();
      }

      // remove filter, if necessary
      if (angular.isDefined(attributes.notSelectable)) {
        angular.element(tpl[0].querySelector('.selected-results')).remove();
        angular.element(tpl[0].querySelector('.results')).remove();
      }

      return tpl[0].outerHTML;
    },
    controllerAs: '$ctrl',
    controller: List,
    link(scope, element, attrs, ngModel) {
      const el = element.find('akam-list-template-placeholder');
      const tplEl = angular.element(listTemplate);
      const itemScope = scope.$parent.$new();
      const akamListTpl = tplEl.find('akam-list-template');

      itemScope.akamList = scope.$ctrl;

      // use text property if no custom markup
      if (attrs.textProperty) {
        el.html(`
          <akam-list-template>
            <span ng-bind="akamList.textPropertyFn(item)"></span>
          </akam-list-template>`
        );
      }

      akamListTpl.replaceWith(el.html());
      el.replaceWith($compile(tplEl)(itemScope));

      scope.$on('akam-list-reload', () => {
        scope.$broadcast('vsRepeatTrigger');
      });
      scope.$ctrl.ngModel = ngModel;

      ngModel.$isEmpty = value => !value || !value.length;
    }
  };
}

akamList.$inject = ['$compile'];
export default akamList;
